<template>
	<a-dialog class="contact">
		<h1>{{ $t("title") }}</h1>
		<ErrorBoundary #boundary="{ hasError }">
			<v-form v-if="!hasError" ref="form" v-model="valid">
				<FormSection
					:title="$t('organizationInformation')"
					:fields="companyFields"
				>
					<TextInput
						v-model="staticFields.company_name"
						required
						:label="$t('organizationName')"
					/>
				</FormSection>
				<FormSection
					:title="$t('personalInformation')"
					:fields="personalFields"
				>
					<Select
						v-model="staticFields.salutation"
						:options="$t('salutations')"
						required
						:label="$t('salutation')"
						class="salutation"
					/>
					<TextInput
						v-model="staticFields.first_name"
						required
						:label="$t('firstName')"
						class="first_name"
					/>
					<TextInput
						v-model="staticFields.last_name"
						required
						:label="$t('lastName')"
						class="last_name"
					/>
					<TextInput
						v-model="staticFields.email"
						:label="$t('emailAddress')"
						email
					/>
					<TextInput
						v-model="staticFields.phone_number"
						:required="false"
						:label="$t('phoneLabel')"
						:placeholder="$t('phonePlaceholder')"
					/>
				</FormSection>
				<v-checkbox
					v-model="staticFields.privacy"
					:rules="privacyRules"
					required
					class="contact__input contact__privacy"
				>
					<i18n slot="label" path="privacyLabel" tag="span">
						<template v-slot:privacy-policy>
							<router-link to="privacy">
								{{ $t("privacyLabelLink") }}
							</router-link>
						</template>
					</i18n>
				</v-checkbox>
			</v-form>
			<div v-else>
				<p>Oops, there are content errors in the contact form.</p>
				<p>Please contact the site administrator.</p>
			</div>
		</ErrorBoundary>
		<div slot="actions" class="contact__actions">
			<a-button :to="{ name: 'summary' }" secondary>
				{{ $t("backToSummary") }}
			</a-button>
			<a-button :error-state="!!error" :loading="submitting" @click="submit">
				<template v-if="!error">{{ $t("submit") }}</template>
				<template v-else>{{ $t("unknown") }}</template>
			</a-button>
		</div>
	</a-dialog>
</template>

<script>
import { ErrorBoundary } from "vue-error-boundary"
import ADialog from "@/components/Dialog";
import FormSection from "@/components/Form/Section";
import Select from "@/components/Form/Select";
import TextInput from "@/components/Form/TextInput";
import aButton from "@/components/Button";
import survey from "@/survey";
import store from "@/store";

export default {
	components: {
		ADialog,
		TextInput,
		FormSection,
		Select,
		aButton,
		ErrorBoundary,
	},
	data() {
		if (!survey.contactDetails) {
			survey.contactDetails = {
				email: "",
				salutation: "",
				first_name: "",
				last_name: "",
				company_name: "",
				privacy: false,
				phone_number: "",
			};
			survey.companyFields = survey.contactFormCompany[this.$i18n.locale]
			survey.personalFields = survey.contactFormPersonal[this.$i18n.locale];
		} else {
			this.$nextTick(() => this.$refs.form.validate());
		}

		return {
			valid: true,
			staticFields: survey.contactDetails,
			submitting: false,
			response: undefined,
			error: undefined,
		};
	},
	computed: {
		locale() {
			return this.$i18n.locale;
		},
		privacyRules() {
			return [v => !!v || this.$t("privacyValidation")];
		},
		companyFields() {
			return survey.companyFields;
		},
		personalFields() {
			return survey.personalFields;
		}
	},
	i18n: {
		messages: {
			en: {
				title: "Ready to receive your results?",
				organizationInformation: "Organisation information",
				organizationName: "Organisation name",
				addItem: "add this",
				personalInformation: "Personal information",
				salutation: "Salutation",
				firstName: "First name",
				lastName: "Last name",
				emailAddress: "Email address",
				privacyLabel: "I agree with the {privacy-policy}",
				privacyLabelLink: "privacy policy",
				privacyValidation: "You must agree to continue!",
				backToSummary: "Back to summary",
				phoneLabel: "Phone number",
				phonePlaceholder: "Ex: +31640070140",
				submit: "Submit",
				salutations: [
					{ text: "Mr", value: "M" },
					{ text: "Ms", value: "F" },
				],
				unknown: "Unknown error, click here to try again",
			},
			nl: {
				title: "Wil je alle resultaten ontvangen?",
				organizationInformation: "Informatie over de organisatie",
				organizationName: "Naam organisatie",
				organizationSector: "Sector organisatie",
				addItem: "voeg toe",
				personalInformation: "Persoonlijke Informatie",
				salutation: "Aanhef ",
				firstName: "Voornaam",
				lastName: "Achternaam",
				emailAddress: "Email adres",
				phoneLabel: "Telefoonnummer",
				phonePlaceholder: "Vb: +31640070140",
				privacyLabel: "Ik ga akkoord met het {privacy-policy}",
				privacyLabelLink: "privacy beleid",
				privacyValidation: "U moet akkoord gaan om verder te kunnen!",
				backToSummary: "Terug naar de samenvatting",
				submit: "Verzenden",
				salutations: [
					{ text: "Heer", value: "M" },
					{ text: "Mevrouw", value: "F" },
				],
				unknown: "Onbekende fout, klik om het opnieuw te proberen",
			}
		}
	},
	methods: {
		async submit() {
			if (!this.$refs.form.validate()) {
				return;
			}
			if (this.submitting) {
				return;
			}
			try {
				this.submitting = true;
				this.error = undefined;
				const formPayload = {
					token: survey.token,
					...this.staticFields,
					contact_organisation: Object.fromEntries(
						survey.companyFields.map(field => [field.name, field.value])
					),
					contact_personal: Object.fromEntries(
						survey.personalFields.map(field => [field.name, field.value])
					),
				}
				const res = await fetch(`${process.env.API}/contact?lang=${this.locale}`, {
						method: "POST",
						headers: {
							Accept: "application/json",
							"Content-Type": "application/json"
						},
						body: JSON.stringify(formPayload),
						credentials: "same-origin"
					}
				);
				if (res.status !== 200 && res.status !== 204) {
					throw new Error(`Invalid status code received: ${res.status}`);
				}
				survey.email = this.staticFields.email;
				this.$router.push({ name: "contactSend" });
			} catch (error) {
				this.error = String(error);
			}
			this.submitting = false;
		},
		translateResponse(currentSection, newSection) {
			const newFields = []

			for (const newField of newSection) {
				const oldField = currentSection.find(field => newField.name === field.name)
				newFields.push({
					...newField,
					value: oldField ? oldField.value : undefined,
				})
			}

			return newFields;
		}
	},
	beforeRouteEnter(to, from, next) {
		if (process.env.VUE_APP_ENV != "DEVELOPMENT" && !survey.answers) {
			next({
				name: "survey",
				params: to.params,
			});
		} else {
			next();
		}
	},
	beforeRouteUpdate(to, from, next) {
		if (to.params.tenant !== from.params.tenant) {
				next({
					name: "survey",
					params: to.params,
				});
				return;
		}

		if (to.params.lang !== from.params.lang) {
			survey.companyFields = this.translateResponse(survey.companyFields, survey.contactFormCompany[to.params.lang]);
			survey.personalFields = this.translateResponse(survey.personalFields, survey.contactFormPersonal[to.params.lang]);
		}

		next();
	}
};
</script>

<style lang="scss">
@import "@/styles/form.scss";

.section {
	.form__field.salutation {
		flex-basis: calc(20% - #{$form-field-margin});
		margin-right: $form-field-margin;
	}

	.form__field.first_name {
		flex-basis: calc(30% - #{$form-field-margin});
		margin-right: $form-field-margin;
	}
}

@media only screen and (max-width: $tablet-breakpoint) {
	.section {
		.form__field.salutation {
			flex-basis: calc(30% - #{$form-field-margin});
		}

		.form__field.first_name {
			flex-basis: calc(70% - #{$form-field-margin});
		}
	}
}

.contact {
	max-width: 1040px;
}

.contact__privacy {
	margin-top: $form-field-margin;
}

.contact_privacy .v-input--selection-controls__ripple {
	margin-top: -4px;
	margin-left: -1px;
}
.contact__autocomplete {
	float: right;
}

.contact__actions {
	width: 100%;
	display: flex;
	justify-content: flex-end;

	> :first-child {
		margin-right: 10px;
	}
}

@media only screen and (max-width: $mobile-breakpoint) {
	.contact__actions {
		flex-direction: column-reverse;

		> :first-child {
			margin: 10px 0 0;
		}
	}
}
</style>
